// MapControls.tsx -- Zoom controls and site-based view mode toolbar.
// Skin: dashboard-overhaul warm tokens (--panel/--card2/--hair2/--acc/--acc-soft/--inkS/--faint).

import { useState, useRef, useEffect } from 'react';
import { SITE_COLOR, SITE_LABEL } from '../../lib/sites';

interface MapControlsProps {
  viewMode: string;
  onViewModeChange: (mode: string) => void;
  sites: string[];
  projectFilter: string | null;
  onProjectFilterChange: (projectId: string | null) => void;
  projects: Array<{ id: string; name: string; color: string }>;
  personFilter?: string | null;
  onPersonFilterChange?: (person: string | null) => void;
  persons?: Array<{ name: string; color: string }>;
  // An Ask answer's "◉ Show in map" deep-links to /map?highlight=<ids>, which dims every other
  // node. That is a filter in every sense the user cares about, but it was invisible to the reset
  // control — so the map sat dimmed with no way to clear it, and the only escape was clicking
  // "Knowledge" in the top nav, which does not read as a reset.
  highlightActive?: boolean;
  onClearHighlight?: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onResetZoom: () => void;
}

export function MapControls({
  viewMode,
  onViewModeChange,
  sites,
  projectFilter,
  onProjectFilterChange,
  projects,
  personFilter,
  onPersonFilterChange,
  persons,
  highlightActive,
  onClearHighlight,
  onZoomIn,
  onZoomOut,
  onResetZoom,
}: MapControlsProps) {
  const btnStyle = (active: boolean): React.CSSProperties => ({
    padding: '4px 10px',
    border: 'none',
    borderRadius: 4,
    cursor: 'pointer',
    fontFamily: "'DM Mono', monospace",
    fontSize: 10,
    fontWeight: active ? 600 : 400,
    textTransform: 'uppercase',
    letterSpacing: '0.12em',
    background: active ? 'var(--acc-soft)' : 'transparent',
    color: active ? 'var(--acc)' : 'var(--inkS)',
    transition: 'background 0.15s, color 0.15s',
  });

  const menuStyle: React.CSSProperties = {
    position: 'absolute',
    top: 'calc(100% + 6px)',
    left: 0,
    minWidth: 160,
    background: 'var(--panel)',
    border: '1px solid var(--hair2)',
    borderRadius: 6,
    padding: 4,
    zIndex: 20,
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    // Cap the menu height and scroll internally so a long project/person list stays
    // inside the graph card (height: min(64vh, 600px), overflow: hidden in MapSurface)
    // instead of running past its bottom edge and being clipped with no way to scroll.
    maxHeight: 280,
    overflowY: 'auto',
    boxShadow: 'var(--sig-shadow)',
  };

  const [siteDropdownOpen, setSiteDropdownOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [personDropdownOpen, setPersonDropdownOpen] = useState(false);
  const siteDropdownRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const personDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!siteDropdownOpen && !dropdownOpen && !personDropdownOpen) return;
    function handleClick(e: MouseEvent) {
      if (siteDropdownOpen && siteDropdownRef.current && !siteDropdownRef.current.contains(e.target as Node)) {
        setSiteDropdownOpen(false);
      }
      if (dropdownOpen && dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (personDropdownOpen && personDropdownRef.current && !personDropdownRef.current.contains(e.target as Node)) {
        setPersonDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [siteDropdownOpen, dropdownOpen, personDropdownOpen]);

  const selectedProject = projectFilter ? projects.find(p => p.id === projectFilter) : null;
  const selectedPerson = personFilter ? persons?.find(p => p.name === personFilter) : null;
  const hasAnyFilter = viewMode !== 'all' || !!projectFilter || !!personFilter || !!highlightActive;

  const resetAll = () => {
    onViewModeChange('all');
    onProjectFilterChange(null);
    onPersonFilterChange?.(null);
    onClearHighlight?.();
  };

  const zoomBtnStyle: React.CSSProperties = {
    width: 32, height: 32,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'var(--panel)', border: '1px solid var(--hair2)',
    borderRadius: 6, cursor: 'pointer', color: 'var(--inkS)',
    fontSize: 16, fontWeight: 500, lineHeight: 1,
    transition: 'background 0.15s, color 0.15s',
  };

  return (
    <>
      {/* View mode toolbar */}
      <div
        style={{
          position: 'absolute',
          top: 16,
          left: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          background: 'var(--panel)',
          border: '1px solid var(--hair2)',
          borderRadius: 6,
          padding: 4,
          zIndex: 10,
          boxShadow: 'var(--sig-shadow)',
        }}
      >
        <div ref={siteDropdownRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setSiteDropdownOpen(!siteDropdownOpen)}
            style={{
              ...btnStyle(viewMode !== 'all'),
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            {viewMode !== 'all' && (
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: SITE_COLOR[viewMode], flexShrink: 0 }} />
            )}
            {viewMode === 'all' ? 'Source' : SITE_LABEL[viewMode]}
            <svg width="8" height="5" viewBox="0 0 8 5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 1L4 4L7 1" />
            </svg>
          </button>

          {siteDropdownOpen && (
            <div style={{ ...menuStyle, minWidth: 140 }}>
              <button
                onClick={() => { onViewModeChange('all'); setSiteDropdownOpen(false); }}
                style={{ ...btnStyle(viewMode === 'all'), textAlign: 'left', width: '100%' }}
              >
                All Sources
              </button>
              {sites.map(site => (
                <button
                  key={site}
                  onClick={() => { onViewModeChange(site); setSiteDropdownOpen(false); }}
                  style={{
                    ...btnStyle(viewMode === site),
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    textAlign: 'left',
                    width: '100%',
                  }}
                >
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: SITE_COLOR[site], flexShrink: 0 }} />
                  {SITE_LABEL[site]}
                </button>
              ))}
            </div>
          )}
        </div>

        <span style={{ width: 1, height: 16, background: 'var(--hair2)', margin: '0 4px' }} />

        <div ref={dropdownRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            style={{
              ...btnStyle(!!projectFilter),
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            {selectedProject ? selectedProject.name : 'Project'}
            <svg width="8" height="5" viewBox="0 0 8 5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 1L4 4L7 1" />
            </svg>
          </button>

          {dropdownOpen && (
            <div style={menuStyle}>
              <button
                onClick={() => { onProjectFilterChange(null); setDropdownOpen(false); }}
                style={{
                  ...btnStyle(projectFilter === null),
                  textAlign: 'left',
                  width: '100%',
                }}
              >
                All Projects
              </button>
              {projects.map(p => (
                <button
                  key={p.id}
                  onClick={() => { onProjectFilterChange(p.id); setDropdownOpen(false); }}
                  style={{
                    ...btnStyle(projectFilter === p.id),
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    textAlign: 'left',
                    width: '100%',
                  }}
                >
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: p.color, flexShrink: 0 }} />
                  {p.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {persons && persons.length > 0 && onPersonFilterChange && (
          <>
            <span style={{ width: 1, height: 16, background: 'var(--hair2)', margin: '0 4px' }} />
            <div ref={personDropdownRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setPersonDropdownOpen(!personDropdownOpen)}
                style={{
                  ...btnStyle(!!personFilter),
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                {selectedPerson ? selectedPerson.name : 'Person'}
                <svg width="8" height="5" viewBox="0 0 8 5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 1L4 4L7 1" />
                </svg>
              </button>

              {personDropdownOpen && (
                <div style={{ ...menuStyle, minWidth: 140 }}>
                  <button
                    onClick={() => { onPersonFilterChange(null); setPersonDropdownOpen(false); }}
                    style={{
                      ...btnStyle(personFilter === null || personFilter === undefined),
                      textAlign: 'left',
                      width: '100%',
                    }}
                  >
                    Everyone
                  </button>
                  {persons.map(p => (
                    <button
                      key={p.name}
                      onClick={() => { onPersonFilterChange(p.name); setPersonDropdownOpen(false); }}
                      style={{
                        ...btnStyle(personFilter === p.name),
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        textAlign: 'left',
                        width: '100%',
                      }}
                    >
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: p.color, flexShrink: 0 }} />
                      {p.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {hasAnyFilter && (
          <>
            <span style={{ width: 1, height: 16, background: 'var(--hair2)', margin: '0 4px' }} />
            <button
              onClick={resetAll}
              style={{
                ...btnStyle(false),
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                color: 'var(--inkS)',
              }}
            >
              Reset
              <svg width="8" height="8" viewBox="0 0 8 8" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <path d="M1 1L7 7M7 1L1 7" />
              </svg>
            </button>
          </>
        )}
      </div>

      {/* Zoom controls: bottom-right */}
      <div
        style={{
          position: 'absolute',
          bottom: 16,
          right: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          zIndex: 10,
        }}
      >
        <button
          onClick={onZoomIn}
          aria-label="Zoom in"
          style={zoomBtnStyle}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--acc-soft)'; e.currentTarget.style.color = 'var(--ink)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--panel)'; e.currentTarget.style.color = 'var(--inkS)'; }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <line x1="8" y1="3" x2="8" y2="13" /><line x1="3" y1="8" x2="13" y2="8" />
          </svg>
        </button>
        <button
          onClick={onResetZoom}
          aria-label="Reset zoom"
          style={{ ...zoomBtnStyle, fontSize: 10, fontFamily: "'DM Mono', monospace" }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--acc-soft)'; e.currentTarget.style.color = 'var(--ink)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--panel)'; e.currentTarget.style.color = 'var(--inkS)'; }}
        >
          1:1
        </button>
        <button
          onClick={onZoomOut}
          aria-label="Zoom out"
          style={zoomBtnStyle}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--acc-soft)'; e.currentTarget.style.color = 'var(--ink)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--panel)'; e.currentTarget.style.color = 'var(--inkS)'; }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <line x1="3" y1="8" x2="13" y2="8" />
          </svg>
        </button>
      </div>
    </>
  );
}
